import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification, Table, Input, Button, Modal, theme } from 'antd';
import {
  PlusOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import {
  ICertificate,
  IICTServices,
  EditedData,
} from 'store/ducks/DataEntry/ICTServices/types';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import useFilters from 'utils/hooks/useFilters';
import DynamicField from 'components/DynamicCalendarField/DynamicField';
import CustomSelect from 'components/CustomSelect';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import { setSelecedEntity_ICT } from 'store/ducks/DataEntry/ICTServices/ICTServiceSlice';
import { requiredFieldsForICTServices } from 'constants/requiredFields';
import SaveButton from 'components/SaveButton/SaveButton';
import useTooltipClickOutside from 'utils/hooks/useTooltipClickOutside';
import { useTranslation } from 'react-i18next';
import * as Styled from '../../DataEntry.styled';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const ICTServices: React.FC = () => {
  const dispatch = useDispatch();
  const { query } = useFilters();
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();

  const [data, setData] = useState<IICTServices[]>([]);
  const [editedData, setEditedData] = useState<EditedData>({});
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const [ictServicesTypes, setICTServicesType] = useState([]);
  const [levelsOfReliance, setLevelsOfReliance] = useState([]);
  const [criticalityAssessments, setCriticalityAssessments] = useState([]);
  const [localRegulatorCriticality, setLocalRegulatorCriticality] = useState(
    [],
  );
  const topScrollRef = useRef<HTMLDivElement>(null);
  const topScrollInnerRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement | null>(null);
  const [systems, setSystems] = useState([]);
  const [ictLevel, setICTLevel] = useState([]);
  const { countries } = useCountries();
  const [possibility, setPossibility] = useState([]);
  const [yesNo, setYesNo] = useState<{ label: string; value: string }[]>([]);

  const [functionNames, setFunctionNames] = useState<
    { label: string; value: string }[]
  >([]);

  const { confirm } = Modal;
  const [impact, setImpact] = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const isLoggedIn = localStorage.getItem('authToken');
  const [isTooltipVisible, setIsTooltipVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const { selectedContractForICT, selectedEntityForICT, contractRefNumber } =
    useSelector((state: any) => state.ictServices);
  const [validities, setValidities] = useState<
    { label: string; value: string }[]
  >([
    { label: 'Valid', value: 'valid' },
    { label: 'Not Valid', value: 'not_valid' },
    { label: 'End of Validity Close', value: 'end_of_validity_close' },
  ]);

  const fetchICT = ict_serviceOp.useFetchICTServices();

  useEffect(() => {
    if (!selectedEntityForICT) {
      const selectedEntity = {
        entityId: query.entityId ? Number(query.entityId) : 0,
        entity: query.entity ? String(query.entity) : '',
      };
      dispatch(setSelecedEntity_ICT(selectedEntity));
    }
  }, [query]);

  const fetchICTServices = async () => {
    try {
      const response = await fetchICT();
      const data = response?.data['ICT services'];
      if (data.length === 0) {
        notification.warning({
          message: '',
          description: response.data.message,
          placement: 'topRight',
          duration: 20,
        });
      }
      const mappedData = data.map((item: any, index: number) => ({
        key: index,
        alternatives_exit_id: item.alternatives_exit.id,
        alternatives_exit_alternative_providers_identified:
          item.alternatives_exit.alternative_providers_identified,
        alternatives_exit_exit_plan_exists: item.exit_plan_exists?.id,
        alternatives_exit_reintegration_possible:
          item.possibility_of_reintegration?.description,
        reintegration_possible_id: item.possibility_of_reintegration?.id,
        alternatives_exit_alternative_ict_tpp_identification:
          item.alternatives_exit.alternative_ict_tpp_identification,
        alternatives_exit_description: item.alternatives_exit.description,
        ictServiceType_id: item.ict_service_type.id,
        ictServiceType_name: item.ict_service_type.name,
        ictServiceType_description: item.ict_service_type.description,
        impactOfDiscontinuingIctServices_id:
          item.impact_of_discontinuing_services.id,
        impactOfDiscontinuingIctServices_description:
          item.impact_of_discontinuing_services.description,
        levelOfRelianceICTService_id: item.level_of_reliance.id,
        levelOfRelianceICTService_name: item.level_of_reliance.name,
        levelOfRelianceICTService_description:
          item.level_of_reliance.description,
        levelofSupportingICTService_id:
          item.level_of_supporting_ict_service?.id,
        levelofSupportingICTService_name:
          item.level_of_supporting_ict_service?.name,
        criticality_assessment_id: item.ict_services_criticality?.id || '',
        criticality_assessment_name: item.ict_services_criticality?.name || '',
        criticality_assessment_description:
          item.ict_services_criticality?.description || '',
        critically_assessment_local_regulator_criticality:
          item.local_regulator_criticality?.name,
        critically_assessment_local_regulator_criticality_id:
          item.local_regulator_criticality?.id,
        ictService_id: item.ict_service_id,
        ictService_ict_service_type_id: item.ict_service_type.id,
        ictService_contractual_arrangement_id: item.contractual_arrangements.id,
        ictService_contractual_arrangement_reference_number:
          item.contractual_arrangements.arrangement_reference_number,
        ictService_contract_name: item.contractual_arrangements.contract_name,
        ictService_description: item.ict_service_description,
        ictService_storage_of_data_id: item.storage_of_data.id,
        ictService_function_id: item.function ? item.function.id : '',
        ictService_country_id: item.country.id,
        ictService_impact_of_discontinuing_ict_services_id:
          item.impact_of_discontinuing_services.id,
        ictService_alternatives_exit_id: item.alternatives_exit.id,
        ictService_level_of_reliance_id: item.level_of_reliance.id,
        ictService_start_date: item.start_date,
        ictService_end_date: item.end_date,
        ict_systems: item.ict_systems
          ? item.ict_systems.map((ict_system: any) => ({
              ict_systems_id: ict_system.id,
              ict_systems_name: ict_system.name,
            }))
          : [],
        certificates: item.certificates
          ? item.certificates.map((certificate: any) => ({
              certificates_id: certificate.id,
              certificates_description: certificate.description,
              certificates_validity: certificate.validity,
              certificates_date_of_issuance: certificate.date_of_issuance,
              certificates_registry: certificate.certificate_registry,
              certificates_date_of_certificate_register:
                certificate.date_of_certificate_register,
              certificates_ict_service_id: item.ictService_id,
              certificates_end_date: certificate.end_date,
              certificates_start_date: certificate.start_date,
            }))
          : [],
        ict_levels: item.ict_service_level
          ? item.ict_service_level.map((level: any) => ({
              ict_level_id: level.id ?? '',
              ict_level_name: level.name,
              ict_level_description: level.description,
            }))
          : [],
        ictService_ict_service_level_id:
          item.ict_service_level?.[0]?.id?.toString() || '',
        ictServiceLevel_id:
          item.ict_levels?.length > 0 ? item.ict_levels[0].id : '',
        ictServiceLevel_description:
          item.ict_levels?.length > 0 ? item.ict_levels[0].description : '',
        ictServiceLevel_name:
          item.ict_levels?.length > 0 ? item.ict_levels[0].name : '',
      }));
      setData(mappedData);
    } catch (error) {
      console.log(error, 'error');
      setData([]);
    }
  };

  const fetchYesNo = async () => {
    try {
      const response = await ict_serviceOp.fetchYesNo();
      if (response?.data.yes_no_statuses) {
        const formattedOptions = response.data.yes_no_statuses.map(
          (reasons: { description: any; id: any }) => ({
            label: reasons.description,
            value: reasons.id,
          }),
        );
        setYesNo(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchICTSystemsCategoryICTServices = async (
    entityId: any,
    entityName: any,
  ) => {
    try {
      const response =
        await ict_serviceOp.fetchICTSystemsCategoryICTServicesbyEntityName({
          entity_id: entityId,
          entity_name: entityName,
        });
      if (response?.data) {
        const formattedOptions = response.data.map(
          (system: { name: any; id: any }) => ({
            label: system.name,
            value: system.id,
          }),
        );
        setSystems(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchICT();
        await Promise.all([
          fetchICTServices(),
          fetchYesNo(),
          fetchICTServiceTypes(),
          fetchLevelsOfReliance(),
          fetchCriticalityAssessments(),
          fetchLocalRegulatorCriticality(),
          fetchICTLevels(),
          fetchReintegrationPossibilities(),
          fetchImpactOfDiscontinuingICTServices(),
          fetchAlternativesExit(),
          fetchFunctionNames(),
          fetchICTSystemsCategoryICTServices(query.entityId, query.entity),
        ]);
      } catch (error) {
        console.log('Error during data fetching:', error);
        notification.error({
          message: 'Info',
          description: 'An error occurred while fetching data.',
          duration: 20,
        });
      }
    };

    const shouldFetch = selectedEntityForICT && selectedContractForICT;

    if (shouldFetch) {
      fetchData();
    }
  }, [selectedEntityForICT, selectedContractForICT, query]);

  useEffect(() => {
    if (query.entity) {
      fetchICTServiceTypes();
      fetchYesNo();
      fetchLevelsOfReliance();
      fetchCriticalityAssessments();
      fetchLocalRegulatorCriticality();
      fetchICTLevels();
      fetchReintegrationPossibilities();
      fetchImpactOfDiscontinuingICTServices();
      fetchAlternativesExit();
      fetchFunctionNames();
      // fetchCountries();
    }
  }, [query]);

  const addNewCertificate = (rowKey: string | number) => {
    const newCertificate: ICertificate = {
      certificates_id: generateUniqueId(),
      certificates_description: '',
      certificates_validity: '',
      certificates_date_of_issuance: '',
      certificates_date_of_certificate_register: '',
      certificates_ict_service_id: '',
      certificates_registry: '',
      certificates_end_date: '9999-01-01',
      certificates_start_date: '',
    };

    setData((prevData) => {
      return prevData.map((record) => {
        if (record.ictService_id === rowKey) {
          return {
            ...record,
            certificates: [...record.certificates, newCertificate],
          };
        }
        return record;
      });
    });
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    rowKey: string,
  ) => {
    const { value } = e.target;

    setEditedData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));

    const updatedRow = data.find(
      (row) => row.ictService_id.toString() == rowKey,
    );

    if (updatedRow) {
      const updatedRowWithChanges: Record<string, any> = {
        ...updatedRow,
        [fieldName]: value,
      };

      if (updatedRow.certificates) {
        const match = fieldName.match(/^(\d+)_newcert_(.+)$/);
        if (match) {
          const idFromFieldName = match[1];
          const actualFieldName = match[2];

          const targetIndex = updatedRow.certificates.findIndex(
            (cert) => cert.certificates_id.toString() === idFromFieldName,
          );

          if (targetIndex !== -1) {
            updatedRowWithChanges.certificates[targetIndex] = {
              ...updatedRow.certificates[targetIndex],
              [actualFieldName]: value,
            };
          }
        }
      }
      const cleanedUpRow = Object.keys(updatedRowWithChanges).reduce(
        (acc: Record<string, any>, key) => {
          const newKey = key.replace(/^[^a-zA-Z]+/, '');
          acc[newKey] = updatedRowWithChanges[key];
          return acc;
        },
        {},
      );

      setChangedData((prevChangedData) => {
        const newChangedData = [...prevChangedData];
        const existingRowIndex = newChangedData.findIndex(
          (row) => row.ictService_id === cleanedUpRow.ictService_id,
        );

        if (existingRowIndex !== -1) {
          const existingRow = newChangedData[existingRowIndex];
          const mergedRow = { ...existingRow, ...cleanedUpRow };
          if (JSON.stringify(existingRow) !== JSON.stringify(mergedRow)) {
            newChangedData[existingRowIndex] = mergedRow;
          }
        } else {
          newChangedData.push(cleanedUpRow);
        }

        return newChangedData;
      });
    }
  };
  const updateChangedData = (editedData: any, changedData: any) => {
    if (editedData && typeof editedData === 'object') {
      Object.keys(editedData).forEach((key: string) => {
        const keyFromEditedData = parseInt(key.split('_')[0]);
        const matchingChangedRow = changedData.find((dataItem: any) => {
          return dataItem.ictService_id === keyFromEditedData;
        });

        if (matchingChangedRow) {
          const fieldName = key.replace(/^\d+_/, '');
          if (matchingChangedRow.hasOwnProperty(fieldName)) {
            matchingChangedRow[fieldName] = editedData[key];
          }
          if (
            fieldName.includes('certificates_start_date') ||
            fieldName.includes('certificates_end_date')
          ) {
            matchingChangedRow[fieldName] =
              editedData[key] !== undefined
                ? String(editedData[key] ?? '')
                : null;
          }
        }
      });
    } else {
      console.log('editedData is not an object:', editedData);
    }

    setChangedData([...changedData]);
  };

  useEffect(() => {
    updateChangedData(editedData, changedData);
  }, [editedData]);

  const dataWithKey = data.map((item) => ({
    ...item,
    key: item.ictService_id,
  }));
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '100'],
    position: ['topRight'] as ['topRight'],
  });

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize,
    });
  };
  const ictService_id = generateUniqueId();

  const addNewRow = () => {
    const newRowTemplate: IICTServices = {
      key: ictService_id,
      isNew: true,
      ictServiceType_id: '',
      ictServiceType_name: '',
      ictServiceType_description: '',
      levelofSupportingICTService_id: '',
      levelofSupportingICTService_name: '',
      ictService_id: ictService_id,
      ictService_ict_service_type_id: '',
      ictService_contractual_arrangement_id: selectedContractForICT?.value,
      ictService_contractual_arrangement_reference_number:
        data[0]?.ictService_contractual_arrangement_reference_number,
      ictService_contract_name: data[0]?.ictService_contract_name,
      ictService_description: '',
      ictService_storage_of_data_id: '',
      ictService_function_id: '',
      ictService_country_id: '',
      ictService_start_date: '',
      ictService_end_date: '9999-01-01',
      ictService_ict_service_level_id: '',
      ictService_impact_of_discontinuing_ict_services_id: '',
      ictService_alternatives_exit_id: '',
      ictService_level_of_reliance_id: '',
      alternatives_exit_id: '',
      alternatives_exit_alternative_providers_identified: '',
      alternatives_exit_exit_plan_exists: '',
      alternatives_exit_reintegration_possible: '',
      reintegration_possible_id: '',
      alternatives_exit_alternative_ict_tpp_identification: '',
      alternatives_exit_description: '',
      impactOfDiscontinuingIctServices_id: '',
      impactOfDiscontinuingIctServices_description: '',
      levelOfRelianceICTService_id: '',
      levelOfRelianceICTService_name: '',
      levelOfRelianceICTService_description: '',
      criticality_assessment_id: '',
      criticality_assessment_name: '',
      criticality_assessment_description: '',
      critically_assessment_local_regulator_criticality: '',
      critically_assessment_local_regulator_criticality_id: '',
      certificates: [],
      ict_systems: [],
      ict_levels: [
        {
          ict_level_id: '',
          ict_level_name: '',
          ict_level_description: '',
        },
      ],
      data_sensitiveness_name: '',
      data_sensitiveness_description: '',
    };
    const newRow = structuredClone(newRowTemplate);

    setData((prevData) => [...prevData, newRow]);
    setChangedData((prevChangedData) => [...prevChangedData, newRow]);
  };
  const handleOpenTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: true }));
  };

  const handleCloseTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: false }));
  };
  useTooltipClickOutside({
    tooltipVisibility: isTooltipVisible,
    setTooltipVisibility: setIsTooltipVisible,
  });

  const fetchICTServiceTypes = async () => {
    try {
      const response = await ict_serviceOp.fetchICTServiceTypes();
      if (response?.data.ict_service_types) {
        const formattedOptions = response.data.ict_service_types.map(
          (types: { name: any; id: any }) => ({
            label: types.name,
            value: types.id,
          }),
        );
        setICTServicesType(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLevelsOfReliance = async () => {
    try {
      const response = await ict_serviceOp.fetchLevelsOfReliance();
      if (response?.data.levels_of_reliance) {
        const formattedOptions = response.data.levels_of_reliance.map(
          (levels: { name: any; id: any }) => ({
            label: levels.name,
            value: levels.id,
          }),
        );
        setLevelsOfReliance(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCriticalityAssessments = async () => {
    try {
      const response = await ict_serviceOp.fetchICTServicesCriticality();
      if (response?.data.ict_services_criticality) {
        const formattedOptions = response.data.ict_services_criticality.map(
          (assessments: { name: any; id: any }) => ({
            label: assessments.name,
            value: assessments.id,
          }),
        );
        setCriticalityAssessments(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLocalRegulatorCriticality = async () => {
    try {
      const response = await ict_serviceOp.fetchLocalRegulatorCriticality();
      if (response?.data.local_regulator_criticality) {
        const formattedOptions = response.data.local_regulator_criticality.map(
          (assessments: { name: any; id: any }) => ({
            label: assessments.name,
            value: assessments.id,
          }),
        );
        setLocalRegulatorCriticality(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchICTLevels = async () => {
    try {
      const response = await ict_serviceOp.fetchICTLevels();
      if (response?.data.ict_service_levels) {
        const formattedOptions = response.data.ict_service_levels.map(
          (lvl: { name: any; id: any }) => ({
            label: lvl.name,
            value: lvl.id,
          }),
        );
        setICTLevel(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchReintegrationPossibilities = async () => {
    try {
      const response = await ict_serviceOp.fetchReintegrationPossibilities(
        query.entityId,
      );
      if (response?.data.possibility_of_reintegration) {
        const formattedOptions = response.data.possibility_of_reintegration.map(
          (possibility: { description: any; id: any }) => ({
            label: possibility.description,
            value: possibility.id,
          }),
        );
        setPossibility(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchImpactOfDiscontinuingICTServices = async () => {
    try {
      const response =
        await ict_serviceOp.fetchImpactOfDiscontinuingICTServices(
          query.entityId,
        );
      if (response?.data.impact_of_discontinuing_ict_services) {
        const formattedOptions =
          response.data.impact_of_discontinuing_ict_services.map(
            (impact: { description: any; id: any }) => ({
              label: impact.description,
              value: impact.id,
            }),
          );
        setImpact(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFunctionNames = async () => {
    try {
      const response = await ict_serviceOp.fetchFunctionsbyEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });
      if (response?.data) {
        const formattedOptions = response.data.map(
          (reasons: { function_name: any; id: any }) => ({
            label: reasons.function_name,
            value: reasons.id,
          }),
        );
        setFunctionNames(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveRow = (ictService_id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      onOk: () => {
        setData((prev) =>
          prev.filter((item) => item.ictService_id !== ictService_id),
        );

        setChangedData((prev) => {
          const removedItem = prev.find(
            (x) => x.ictService_id === ictService_id,
          );

          if (removedItem?.isNew) {
            return prev.filter((x) => x.ictService_id !== ictService_id);
          }

          return prev.map((x) =>
            x.ictService_id === ictService_id ? { ...x, _delete: true } : x,
          );
        });

        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };

  const handleInactivate = (record: IICTServices) => {
    if (!record.ictService_id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: Number(record.ictService_id),
      tableName: 'ict_service',
      onSuccess: async () => {
        await fetchICTServices();
      },
    });
  };

  const fetchAlternativesExit = async () => {
    try {
      const response = await ict_serviceOp.fetchAlternativesExit();
      if (response?.data.alternatives_exit) {
        const formattedOptions = response.data.alternatives_exit.map(
          (alternatives: {
            alternative_ict_tpp_identification: any;
            id: any;
            exit_plan_exists: boolean;
          }) => ({
            label: alternatives.alternative_ict_tpp_identification,
            exists: alternatives.exit_plan_exists,
            value: alternatives.id,
          }),
        );
        setAlternatives(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmit = async () => {
    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'No data has been changed',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = changedData.some((item) =>
      (requiredFieldsForICTServices as (keyof typeof item)[]).some((field) => {
        return item[field] === '' || item[field] === null;
      }),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a start date and criticality assessment, country, ict service level and local regulator criticality fields before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      contractual_arrangement_id: selectedContractForICT?.value,
      entity_name: selectedEntityForICT?.label,

      ict_service_entries: changedData.map((item) => ({
        id: item.ictService_id,
        ict_service_type_id: item.ictService_ict_service_type_id,
        ictService_contractual_arrangement_id:
          item.ictService_contractual_arrangement_id,
        ictService_contractual_arrangement_reference_number:
          item.ictService_contractual_arrangement_reference_number,
        ictService_contract_name: item.ictService_contract_name,
        description: item.ictService_description,
        storage_of_data_id: item.ictService_storage_of_data_id,
        function_id: item.ictService_function_id,
        country_id: item.ictService_country_id,
        ict_service_level_id:
          item.ictServiceLevel_id ||
          (item.ict_levels ? item.ict_levels[0]?.ict_level_id : undefined),
        impact_of_discontinuing_ict_services_id:
          item.ictService_impact_of_discontinuing_ict_services_id,
        alternatives_exit_id: item.ictService_alternatives_exit_id,
        level_of_reliance_id: item.ictService_level_of_reliance_id,
        criticality_assessment_id: item.criticality_assessment_id,
        start_date: item.ictService_start_date,
        end_date: item.ictService_end_date,
        exit_plan_exists_id: item.alternatives_exit_exit_plan_exists,
        possibility_of_reintegration_id: item.reintegration_possible_id,
        ict_services_criticality_id: item.criticality_assessment_id,
        local_regulator_criticality_id:
          item.critically_assessment_local_regulator_criticality_id,
        contractual_arrangement_id: selectedContractForICT?.value,
        alternative_ict_tpp_identification:
          item.alternative_ict_tpp_identification,
        ict_system_ids: item.ict_system_id
          ? item.ict_system_id
          : item.ict_systems
            ? item.ict_systems.map(
                (ict: { ict_systems_id: string }) => ict.ict_systems_id,
              )
            : [],
      })),
      certificates: changedData
        .map((item) =>
          item.certificates.map((cert: ICertificate) => ({
            id: cert.certificates_id,
            description: cert.certificates_description,
            validity: cert.certificates_validity,
            date_of_issuance: cert.certificates_date_of_issuance,
            certificate_registry: cert.certificates_registry,
            date_of_certificate_register:
              cert.certificates_date_of_certificate_register,
            end_date: cert.certificates_end_date,
            start_date: cert.certificates_start_date,
            ict_service_id: item.ictService_id,
          })),
        )
        .flat(),

      token: isLoggedIn,
    };
    try {
      const response = await ict_serviceOp.postICTServices(formattedData);
      setData((prev) =>
        prev.map((item) => ({ ...item, isNew: false, isEdited: false })),
      );
      setChangedData([]);
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns: EditableColumnType<IICTServices>[] = [
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.CONTRACT')}</p>
            <p>B.02.03.0010</p>
          </div>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.CONTRACT_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictService_contractual_arrangement_reference_number',
      editable: true,
      fixed: 'left',
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        return (
          <Input
            value={
              record?.ictService_contractual_arrangement_reference_number ??
              contractRefNumber
            }
            disabled
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p> {t('ICTS_SERVICES.CONTRACT_NAME')}</p>
          </div>
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictService_contract_name',
      editable: true,
      fixed: 'left',
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        console.log(record, 'record?.ictService_contract_name');
        return <Input value={record?.ictService_contract_name} disabled />;
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.FUNCTION_NAME')}</p>
            <p>B.06.01.0030</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.FUNCTION_NAME_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictService_function_id',
      editable: true,
      fixed: 'left',
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictService_function_id`;
        const rowKey = record.key;
        return (
          <Styled.ColourReportredFields>
            <CustomSelect
              options={functionNames}
              placeholder="Select an option"
              value={
                editedData.hasOwnProperty(fieldName)
                  ? editedData[fieldName]
                  : record.ictService_function_id
              }
              onChange={(value: string) => {
                const event = {
                  target: {
                    value,
                  },
                } as React.ChangeEvent<HTMLInputElement>;

                handleEditChange(event, fieldName, rowKey);
              }}
              size="large"
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Styled.ColourReportredFields>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('ICTS_SERVICES.COUNTRY_PERFORMED')}
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictService_country_id',
      editable: false,
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictService_country_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={countries}
            placeholder="Select an option"
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ictService_country_id
            }
            onChange={(value: string) => {
              const event = {
                target: {
                  value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.ICT_SERVICES_DESCRIPTION')}{' '}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'ictService_description',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictService_description`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ictService_description
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.ICT_SERVICE_LEVEL')}{' '}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'ictServiceLevel_name',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictServiceLevel_name`;
        const fieldId = `${record.ictService_id}_ictServiceLevel_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={ictLevel}
            placeholder="Select an option"
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ict_levels[0].ict_level_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const eventName = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventName, fieldName, rowKey);
              const eventId = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventId, fieldId, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.TYPE_OF_SERVICE')}</p>
            <p>B.07.01.0040</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.TYPE_OF_SERVICE_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictServiceType_name',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictServiceType_name`;
        const fieldNameId = `${record.ictService_id}_ictServiceType_id`;
        const fieldNameId2 = `${record.ictService_id}_ictService_ict_service_type_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={ictServicesTypes}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ictServiceType_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const event = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);

              const eventId = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventId, fieldNameId, rowKey);
              handleEditChange(eventId, fieldNameId2, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.EXIT_PLAN_EXISTS')}</p>
            <p>B.07.01.0080</p>
          </div>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.EXIT_PLAN_EXISTS_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'alternatives_exit_exit_plan_exists',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_alternatives_exit_exit_plan_exists`;
        const rowKey = record.key;

        const displayValue = editedData.hasOwnProperty(fieldName)
          ? editedData[fieldName] === 1
            ? 'Yes'
            : editedData[fieldName] === 2
              ? 'No'
              : ''
          : record.alternatives_exit_exit_plan_exists === 1
            ? 'Yes'
            : record.alternatives_exit_exit_plan_exists === 2
              ? 'No'
              : '';
        return (
          <CustomSelect
            options={yesNo}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={displayValue}
            onChange={(
              value: string,
              option: { label: string; value: number },
            ) => {
              const selectedValue = option.label === 'Yes' ? 1 : 2;

              const event = {
                target: {
                  value: selectedValue,
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.POSSIBILITY_OF_REINTEGRATION')}</p>
            <p>B.07.01.0090</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.POSSIBILITY_OF_REINTEGRATION_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'alternatives_exit_reintegration_possible',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_alternatives_exit_reintegration_possible`;
        const fieldId = `${record.ictService_id}_reintegration_possible_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={possibility}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.alternatives_exit_reintegration_possible
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const event = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);
              const eventId = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventId, fieldId, rowKey);

              const eventID = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('ICTS_SERVICES.ALTERNATIVE')}
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.ALTERNATIVE_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'alternatives_exit_alternative_ict_tpp_identification',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_alternatives_exit_alternative_ict_tpp_identification`;
        const rowKey = record.key;
        const fieldId = `${record.ictService_id}_alternatives_exit_id`;
        const fieldId1 = `${record.ictService_id}_ictService_alternatives_exit_id`;
        return (
          <CustomSelect
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.alternatives_exit_alternative_ict_tpp_identification
            }
            onChange={(value: string) => {
              const event = {
                target: {
                  value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);
              handleEditChange(event, fieldId, rowKey);
              handleEditChange(event, fieldId1, rowKey);
            }}
            options={alternatives}
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.IMPACT_OF_DISCONTINUING')}</p>
            <p>B.07.01.0100</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.IMPACT_OF_DISCONTINUING_INFO')}
          />
        </Styled.TitleComponent>
      ),

      dataIndex: 'impactOfDiscontinuingIctServices_description',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_impactOfDiscontinuingIctServices_description`;
        const fieldNameID = `${record.ictService_id}_impactOfDiscontinuingIctServices_id`;
        const fieldNameIDICT = `${record.ictService_id}_ictService_impact_of_discontinuing_ict_services_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={impact}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.impactOfDiscontinuingIctServices_description
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const idEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(idEvent, fieldNameID, rowKey);
              handleEditChange(idEvent, fieldNameIDICT, rowKey);
              const nameEvent = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.CRITICALITY_ASSESSMENT')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'criticality_assessment_name',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_criticality_assessment_name`;
        const fieldId = `${record.ictService_id}_criticality_assessment_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={criticalityAssessments}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.criticality_assessment_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const idEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(idEvent, fieldId, rowKey);

              const nameEvent = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.LOCAL_CRITICALITY')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'critically_assessment_local_regulator_criticality',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_critically_assessment_local_regulator_criticality`;
        const fieldId = `${record.ictService_id}_critically_assessment_local_regulator_criticality_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={localRegulatorCriticality}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.critically_assessment_local_regulator_criticality
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const event = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);

              const eventId = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventId, fieldId, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.ICT_SYSTEMS')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'ict_system',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ict_system_name`;
        const fieldId = `${record.ictService_id}_ict_system_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={systems}
            mode="multiple"
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ict_systems?.map(
                    (ict: { ict_systems_name: any }) => ict.ict_systems_name,
                  ) || []
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const event = {
                target: {
                  value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldId, rowKey);
              const eventName = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(eventName, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: <>{t('ICTS_SERVICES.CERTIFICATES')}</>,

      dataIndex: 'certificates',
      key: 'certificates',
      render: (_, record) => {
        return (
          <div>
            {(record.certificates as ICertificate[]).map(
              (cert: ICertificate) => {
                const idFieldName = `${cert.certificates_id}_newcert_certificates_id`;
                const descriptionFieldName = `${cert.certificates_id}_newcert_certificates_description`;
                const registryFieldName = `${cert.certificates_id}_newcert_certificates_registry`;
                const issuanceDateFieldName = `${cert.certificates_id}_newcert_certificates_date_of_issuance`;
                const validityFieldName = `${cert.certificates_id}_newcert_certificates_validity`;
                const dateOfRegisterFieldName = `${cert.certificates_id}_newcert_certificates_date_of_certificate_register`;
                const startDateFieldName = `${cert.certificates_id}_newcert_certificates_start_date`;
                const endDateFieldName = `${cert.certificates_id}_newcert_certificates_end_date`;
                const fieldNameServices = `${cert.certificates_id}_newcert_certificates_ict_service_id`;
                const rowKey = record.key;
                const handleRemoveCertificates = (
                  certificateKey: number,
                  certificateID: number,
                ) => {
                  Modal.confirm({
                    title: t(
                      'ICTS_SERVICES.REMOVE_CERTIFICATE_CONFIRMATION_TITLE',
                    ),
                    content: t(
                      'ICTS_SERVICES.REMOVE_CERTIFICATE_CONFIRMATION_CONTENT',
                    ),
                    okText: t('GENERAL.YES'),
                    cancelText: t('GENERAL.NO'),
                    onOk: async () => {
                      try {
                        deleteRecord({
                          id: certificateID,
                          tableName: 'certificates',
                          onSuccess: async () => {
                            const updatedCertificates = data.map(
                              (certificate) => {
                                if (certificate.key === certificateKey) {
                                  return {
                                    ...certificate,
                                    certificates:
                                      certificate.certificates.filter(
                                        (certificate: ICertificate) =>
                                          certificate.certificates_id !==
                                          certificateID,
                                      ),
                                  };
                                }
                                return certificate;
                              },
                            );
                            setData(updatedCertificates);
                            if (record.isNew) {
                              const updatedCertificates = data.map((item) => {
                                const filteredCertificates =
                                  item.certificates.filter(
                                    (certificate: ICertificate) =>
                                      certificate.certificates_id !==
                                      cert.certificates_id,
                                  );
                                return {
                                  ...item,
                                  certificates: filteredCertificates,
                                };
                              });

                              setData(updatedCertificates);
                            } else {
                              fetchICTServices();
                            }
                          },
                        });
                        // const response = await tppOp.inactivateCertificates([
                        //   certificateID,
                        // ]);
                      } catch (error) {
                        console.log('Failed to remove Certificate:', error);
                      }
                    },
                    onCancel: () => {
                      notification.info({
                        message: 'Info',
                        description: t(
                          'ICTS_SERVICES.CERTIFICATE_REMOVAL_CANCELLED',
                        ),
                        duration: 10,
                      });
                    },
                  });
                };
                return (
                  <Styled.TooltipContainer
                    key={cert.certificates_id}
                    visible={isTooltipVisible[idFieldName]}
                    title={
                      <div>
                        <Styled.TooltipButtonContainer>
                          <Button
                            size="small"
                            type="text"
                            danger
                            onClick={() =>
                              handleRemoveCertificates(
                                record.key,
                                cert.certificates_id,
                              )
                            }
                          >
                            <DeleteOutlined />
                          </Button>
                          <Button
                            size="small"
                            type="text"
                            onClick={() => handleCloseTooltip(idFieldName)}
                          >
                            {t('GENERAL.ADD_AMEND')}
                          </Button>
                        </Styled.TooltipButtonContainer>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.REGISTER')}{' '}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <Input
                            value={
                              editedData.hasOwnProperty(registryFieldName)
                                ? typeof editedData[registryFieldName] ===
                                  'boolean'
                                  ? editedData[registryFieldName]
                                    ? 'true'
                                    : 'false'
                                  : String(editedData[registryFieldName])
                                : String(cert.certificates_registry)
                            }
                            onChange={(e) =>
                              handleEditChange(e, registryFieldName, rowKey)
                            }
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.DATE_OF_ISSUANCE')}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <DynamicField
                            value={
                              editedData.hasOwnProperty(issuanceDateFieldName)
                                ? String(
                                    editedData[issuanceDateFieldName] ?? '',
                                  )
                                : String(
                                    cert.certificates_date_of_issuance ?? '',
                                  )
                            }
                            fieldName={issuanceDateFieldName}
                            rowKey={record.key}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleEditChange(
                                e,
                                issuanceDateFieldName,
                                record.key,
                              )
                            }
                            type="date"
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.VALIDITY')}{' '}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <CustomSelect
                            options={validities}
                            value={cert.certificates_validity}
                            onChange={(value: string) => {
                              const selectedValidity = validities.find(
                                (v) => v.value === value,
                              );
                              const event = {
                                target: {
                                  value,
                                },
                              } as React.ChangeEvent<HTMLInputElement>;

                              handleEditChange(
                                event,
                                validityFieldName,
                                rowKey,
                              );
                            }}
                            filterOption={(
                              input: string,
                              option: { label: string },
                            ) =>
                              option?.label
                                ?.toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {' '}
                            {t('ICTS_SERVICES.CERTIFICATE_DESCRIPTION')}{' '}
                          </strong>
                          <Input
                            value={
                              editedData.hasOwnProperty(descriptionFieldName)
                                ? typeof editedData[descriptionFieldName] ===
                                  'boolean'
                                  ? editedData[descriptionFieldName]
                                    ? 'true'
                                    : 'false'
                                  : String(editedData[descriptionFieldName])
                                : String(cert.certificates_description)
                            }
                            onChange={(e) =>
                              handleEditChange(e, descriptionFieldName, rowKey)
                            }
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.CERTIFICATE_VALID_FROM')}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <DynamicField
                            value={
                              editedData.hasOwnProperty(startDateFieldName)
                                ? String(editedData[startDateFieldName] ?? '')
                                : String(cert.certificates_start_date ?? '')
                            }
                            fieldName={startDateFieldName}
                            rowKey={record.key}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleEditChange(
                                e,
                                startDateFieldName,
                                record.key,
                              )
                            }
                            type="date"
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.CERTIFICATE_VALID_TO')}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <DynamicField
                            value={
                              editedData.hasOwnProperty(endDateFieldName)
                                ? String(
                                    editedData[endDateFieldName] ??
                                      '9999-01-01',
                                  )
                                : String(
                                    cert.certificates_end_date ?? '9999-01-01',
                                  )
                            }
                            fieldName={endDateFieldName}
                            rowKey={record.key}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleEditChange(e, endDateFieldName, record.key)
                            }
                            type="date"
                            useDefaultDate={true}
                          />
                        </Styled.InputField>
                        <Styled.InputField>
                          <strong>
                            {t('ICTS_SERVICES.DATE_OF_REGISTER')}
                            <Styled.Asterisx>*</Styled.Asterisx>
                          </strong>
                          <DynamicField
                            value={
                              editedData.hasOwnProperty(dateOfRegisterFieldName)
                                ? String(
                                    editedData[dateOfRegisterFieldName] ?? '',
                                  )
                                : String(
                                    cert.certificates_date_of_certificate_register ??
                                      '',
                                  )
                            }
                            fieldName={dateOfRegisterFieldName}
                            rowKey={record.key}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleEditChange(
                                e,
                                dateOfRegisterFieldName,
                                record.key,
                              )
                            }
                            type="date"
                          />
                        </Styled.InputField>
                      </div>
                    }
                    open={isTooltipVisible[idFieldName] || false}
                    trigger={[]}
                  >
                    <Styled.TagComponent
                      color="blue"
                      onClick={() => handleOpenTooltip(idFieldName)}
                    >
                      <Input
                        value={
                          editedData.hasOwnProperty(descriptionFieldName)
                            ? typeof editedData[descriptionFieldName] ===
                              'boolean'
                              ? editedData[descriptionFieldName]
                                ? 'true'
                                : 'false'
                              : String(editedData[descriptionFieldName])
                            : String(cert.certificates_description)
                        }
                        readOnly
                        addonAfter={<FolderOpenOutlined />}
                      />
                    </Styled.TagComponent>
                    <Styled.GlobalStyle />
                  </Styled.TooltipContainer>
                );
              },
            )}
            <Styled.AddButton
              type="text"
              icon={<PlusOutlined />}
              onClick={() => addNewCertificate(record.key)}
            />
          </div>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p> {t('ICTS_SERVICES.DATE_OF_REGISTER')} </p>
            <p>B.02.02.0140</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.DATE_OF_REGISTER_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'ictService_storage_of_data_id',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_ictService_storage_of_data_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={yesNo}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.ictService_storage_of_data_id
            }
            onChange={(value: string) => {
              const event = {
                target: {
                  value,
                },
              } as React.ChangeEvent<HTMLInputElement>;

              handleEditChange(event, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <>
          {t('ICTS_SERVICES.ICT_SERVICE_VALID_FROM_TO')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </>
      ),
      dataIndex: 'ictService_date_range',
      render: (_: string, record) => {
        const startFieldName = `${record.ictService_id}_ictService_start_date`;
        const endFieldName = `${record.ictService_id}_ictService_end_date`;
        const rowKey = record.key;

        return (
          <Styled.DateInputs>
            <Styled.InputField>
              <DynamicField
                value={
                  editedData.hasOwnProperty(startFieldName)
                    ? String(editedData[startFieldName] ?? '')
                    : String(record.ictService_start_date ?? '')
                }
                fieldName={startFieldName}
                rowKey={rowKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleEditChange(e, startFieldName, rowKey)
                }
                type="date"
              />
            </Styled.InputField>

            <Styled.InputField>
              <DynamicField
                value={
                  editedData.hasOwnProperty(endFieldName)
                    ? String(editedData[endFieldName] ?? '9999-01-01')
                    : String(record.ictService_end_date ?? '9999-01-01')
                }
                fieldName={endFieldName}
                rowKey={rowKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleEditChange(e, endFieldName, rowKey)
                }
                type="date"
                useDefaultDate={true}
              />
            </Styled.InputField>
          </Styled.DateInputs>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('ICTS_SERVICES.LEVEL_OF_RELIANCE')}</p>
            <p>B.02.02.0180</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('ICTS_SERVICES.LEVEL_OF_RELIANCE_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'levelOfRelianceICTService_name',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.ictService_id}_levelOfRelianceICTService_name`;
        const fieldId = `${record.ictService_id}_levelOfRelianceICTService_id`;
        const fieldIdICT = `${record.ictService_id}_ictService_level_of_reliance_id`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={levelsOfReliance}
            placeholder={t('ICTS_SERVICES.SELECT_OPTION')}
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.levelOfRelianceICTService_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const idEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(idEvent, fieldId, rowKey);
              handleEditChange(idEvent, fieldIdICT, rowKey);
              const nameEvent = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: t('GENERAL.ACTIONS'),
      key: 'actions',
      render: (_: any, record) => (
        <>
          {record.isNew ? (
            <Button danger onClick={() => handleRemoveRow(record.key)}>
              {t('GENERAL.REMOVE')}
            </Button>
          ) : (
            <Button
              danger
              onClick={() => handleInactivate(record as IICTServices)}
            >
              {t('GENERAL.SOFT_DELETE')}
            </Button>
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    const findScrollable = () => {
      const el = document.querySelector(
        '.ant-table-body, .ant-table-content',
      ) as HTMLDivElement | null;

      if (el) {
        tableContentRef.current = el;
      }
    };

    findScrollable();
    const observer = new MutationObserver(findScrollable);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const table = tableContentRef.current;

    if (!top || !table) return;

    const syncTop = () => {
      if (top) top.scrollLeft = table.scrollLeft;
    };
    const syncBottom = () => {
      if (table) table.scrollLeft = top.scrollLeft;
    };

    top.addEventListener('scroll', syncBottom);
    table.addEventListener('scroll', syncTop);

    return () => {
      top.removeEventListener('scroll', syncBottom);
      table.removeEventListener('scroll', syncTop);
    };
  }, [dataWithKey, columns]);

  useEffect(() => {
    const table = tableContentRef.current;
    if (table && topScrollInnerRef.current) {
      topScrollInnerRef.current.style.width = `${table.scrollWidth}px`;
    }
  }, [dataWithKey, columns]);

  return (
    <>
      <Styled.TooltipTableContainer>
        <Styled.TopScrollBar ref={topScrollRef}>
          <div ref={topScrollInnerRef} />
        </Styled.TopScrollBar>
        <Table
          dataSource={dataWithKey}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          className="coloringForTheReports"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: pagination.showSizeChanger,
            pageSizeOptions: pagination.pageSizeOptions,
            position: pagination.position,
          }}
          columns={columns}
          locale={
            query.entity && selectedContractForICT
              ? {
                  emptyText: (
                    <Button type="primary" onClick={() => addNewRow()}>
                      {t('GENERAL.ADD_FIRST_ROW')}
                    </Button>
                  ),
                }
              : undefined
          }
        />
      </Styled.TooltipTableContainer>
      <Styled.ButtonRow>
        {query.entity && selectedContractForICT && dataWithKey.length > 0 && (
          <Styled.AreaButton
            token={token}
            type="primary"
            onClick={() => addNewRow()}
          >
            Add new row
          </Styled.AreaButton>
        )}
        {dataWithKey.length > 0 && (
          <SaveButton
            token={token}
            hasChanges={changedData.length > 0}
            onClick={handleSubmit}
          />
        )}
      </Styled.ButtonRow>
    </>
  );
};

export default ICTServices;
